// collector/db.js
//
// The collector's storage on the phone (collector-app P2-B, locked answer L-I):
// ONE IndexedDB database, `poolit-collector` v1, four stores —
//
//   day        key `planId`   { planId, userId, doc, etag, fetchedAt }
//                             the day document as the server last sent it
//   actions    key `id`       a tap exactly as the action layer built it
//                             { id, planId, planItemId, seq, clientAt, type, payload }
//                             + { userId, status, attempts, lastError, current, answeredAt, inRead }
//                             status: pending | applied | already_applied | conflict | refused
//                             `inRead` = a day read that started after the answer landed
//                             (the tap no longer needs re-applying on top of it)
//   photos     key `uuid`     the collector's own photos (collector-app P2-C):
//                             { uuid, planId, lineId, userId, kind, blob, status,
//                               attempts, lastError, createdAt }
//                             kind `shelf`: a picture for the leader, taken at a
//                             shelf, shrunk on the phone, uploaded when the taps
//                             are all sent (status `queued`); once the server has
//                             it the row keeps only { status: `uploaded`, id, url }
//                             — the Blob is deleted (the row goes at plan close).
//                             kind `invoice`: ONE row per shop, key
//                             `invoice-<plan>-<supplier>` (+ supplierId), in one
//                             of two states (collector-offline P1-A):
//                               `held`   the paper's photo is kept, nothing is
//                                        filed. Owes the server nothing: it goes
//                                        with the plan at plan close.
//                               `queued` the shop's invoice is filed ON THE
//                                        PHONE and not sent yet
//                                        (collector/invoiceQueue.js), with
//                                        { supplierId, amountCents | null,
//                                          noPaper, filedAt, attempts, lastError }
//                                        and a `blob` that may be null (no paper
//                                        photographed). Work the phone owes: it
//                                        keeps its plan's day through plan close
//                                        until it is sent. `filedAt` names the
//                                        filing — a refile, or a new paper put
//                                        on it, is a new `filedAt` on the same
//                                        row, so the answer to a copy already on
//                                        the wire never removes what he changed
//                                        since. A retry rewrites nothing (the
//                                        Blob would be copied each time), so
//                                        `attempts` stays 0 on the phone.
//                             Never uploaded by the photo queue — the paper
//                             rides the filing request.
//   conflicts  key `id`       a card the collector has not dismissed yet
//
// A raw wrapper, NOT a dependency (L-B: `idb` is on disk only transitively).
// What makes a raw wrapper safe here (pwa-workbox-expert review, 30 Sep):
//   - every request of a transaction is issued synchronously inside `work`,
//     never across an await — IndexedDB auto-commits an idle transaction;
//   - a write resolves on the transaction's `complete`, and fails on `abort`
//     (a QuotaExceededError aborts the whole transaction; a request's own
//     `error` alone would miss it);
//   - taps and cards are written with `durability: 'strict'` (flushed to disk,
//     not only to the OS) — an engine without the option ignores it;
//   - `indexedDB.open` gets a ceiling (it can hang after a phone restart): a
//     slow open fails THIS call, and the next call waits on the same open;
//   - a connection the browser closed (WebKit in the background, "connection
//     lost", InvalidStateError) is reopened once; `versionchange` closes ours;
//   - no index is ever read for a key that can be null (IndexedDB leaves such
//     a row out of the index) — the stores are small, rows are read whole and
//     filtered here.
//
// The same interface has an in-memory twin (`createMemoryStorage`) with the
// same copy semantics (structured clone on the way in and out). The unit tests
// run on it (happy-dom has no IndexedDB), and the app falls back to it when
// the browser refuses IndexedDB outright (an old private window) — logged,
// never thrown at a tap.
//
// Cleared only at plan close (`clearPlan`), never on a 401 or a logout (L-I).

export const DB_NAME = 'poolit-collector';
export const DB_VERSION = 1;

export const STORES = Object.freeze({
  DAY: 'day',
  ACTIONS: 'actions',
  PHOTOS: 'photos',
  CONFLICTS: 'conflicts',
});

export const ACTION_STATUS = Object.freeze({
  PENDING: 'pending',
  APPLIED: 'applied',
  ALREADY_APPLIED: 'already_applied',
  CONFLICT: 'conflict',
  REFUSED: 'refused',
});

/** A photo row that reached the server: only its id and url are kept, the Blob is gone. */
export const PHOTO_UPLOADED = 'uploaded';
/**
 * Waiting to be sent: a shelf photo (after the taps, then its own upload), or a
 * shop's invoice filed on the phone (after the taps, by collector/invoiceQueue.js).
 */
export const PHOTO_QUEUED = 'queued';
/** An invoice photo kept until its shop's invoice is filed. */
export const PHOTO_HELD = 'held';

export const PHOTO_KIND = Object.freeze({
  SHELF: 'shelf',
  INVOICE: 'invoice',
});

/** The ONE row a shop's invoice has in `photos` — its paper, and its filing while that waits. */
export function invoiceRowKey(planId, supplierId) {
  return `invoice-${planId}-${supplierId}`;
}

/**
 * The name (`filedAt`) of the filing that replaces a stored one: one
 * millisecond after it, whatever the phone's clock says now.
 */
export function filedJustAfter(filedAt) {
  return new Date((Date.parse(filedAt) || 0) + 1).toISOString();
}

const OPEN_TIMEOUT_MS = 3_000;
/** Flushed to disk, not only to the OS (an engine without the option ignores it). */
export const STRICT = { durability: 'strict' };

/** Ids are strings on the wire (JSON:API) — compare them as strings. */
export function sameId(a, b) {
  return a != null && b != null && String(a) === String(b);
}

function belongsTo(row, userId) {
  if (userId == null) return row?.userId == null;
  return sameId(row?.userId, userId);
}

// ── IndexedDB ────────────────────────────────────────────────────────────────

function upgrade(db) {
  if (!db.objectStoreNames.contains(STORES.DAY)) {
    db.createObjectStore(STORES.DAY, { keyPath: 'planId' });
  }
  if (!db.objectStoreNames.contains(STORES.ACTIONS)) {
    const actions = db.createObjectStore(STORES.ACTIONS, { keyPath: 'id' });
    actions.createIndex('planId', 'planId');
    actions.createIndex('planItemId', 'planItemId');
    actions.createIndex('seq', 'seq');
    actions.createIndex('status', 'status');
  }
  if (!db.objectStoreNames.contains(STORES.PHOTOS)) {
    const photos = db.createObjectStore(STORES.PHOTOS, { keyPath: 'uuid' });
    photos.createIndex('planId', 'planId');
    photos.createIndex('lineId', 'lineId');
    photos.createIndex('status', 'status');
  }
  if (!db.objectStoreNames.contains(STORES.CONFLICTS)) {
    db.createObjectStore(STORES.CONFLICTS, { keyPath: 'id' });
  }
}

function timeout(promise, ms, label) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_resolve, reject) => {
      timer = setTimeout(() => reject(new DOMException(`${label} took over ${ms} ms`, 'TimeoutError')), ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

/** The browser would not open the database at all (not merely slowly). */
function openRefused(error) {
  const refusal = error ?? new DOMException('indexedDB.open failed', 'UnknownError');
  try {
    refusal.openRefused = true;
  } catch {
    /* a frozen error object: the name still says enough */
  }
  return refusal;
}

/** The browser closed the connection under us: reopen once and try again. */
function connectionLost(error) {
  const name = error?.name;
  if (name === 'InvalidStateError' || name === 'UnknownError') return true;
  return /connection.*(lost|clos)/i.test(String(error?.message ?? ''));
}

/**
 * One transaction. `work(tx, out)` issues its requests synchronously and puts
 * what it read on `out.value`; the promise settles on `complete` / `abort`.
 */
function transact(db, stores, mode, work, options) {
  return new Promise((resolve, reject) => {
    let tx;
    try {
      tx = options ? db.transaction(stores, mode, options) : db.transaction(stores, mode);
    } catch (error) {
      if (!options || error?.name !== 'TypeError') {
        reject(error);
        return;
      }
      try {
        tx = db.transaction(stores, mode); // an engine that refuses the options bag
      } catch (fallbackError) {
        reject(fallbackError);
        return;
      }
    }
    const out = { value: undefined, error: null };
    tx.oncomplete = () => resolve(out.value);
    tx.onabort = () => reject(out.error ?? tx.error ?? new DOMException('Transaction aborted', 'AbortError'));
    try {
      work(tx, out);
    } catch (error) {
      // A DataCloneError here is a programming error (a Vue proxy in a row) —
      // the caller logs it; the transaction must not half-commit.
      out.error = error;
      try {
        tx.abort();
      } catch {
        /* already finished */
      }
    }
  });
}

/** Read every row of a store into `out[key]` (inside the running transaction). */
export function readAll(tx, storeName, out, key) {
  const request = tx.objectStore(storeName).getAll();
  request.onsuccess = () => {
    out.value = out.value ?? {};
    out.value[key] = request.result ?? [];
  };
}

/**
 * One IndexedDB database behind the rules at the top of this file: the open
 * gets a ceiling (a slow open fails THIS call, the next call waits on the same
 * open), a failed open is not cached, `versionchange` closes ours, and a
 * connection the browser closed is reopened once. Shared with the preview
 * copies (collector/previewStore.js), which live in a database of their own.
 *
 * @param {IDBFactory} factory
 * @param {{ name: string, version: number, upgrade: (db: IDBDatabase) => void, label?: string }} spec
 * @returns {{ run: (stores: string|string[], mode: IDBTransactionMode, work: Function, options?: object) => Promise<any> }}
 *   `run` = one transaction (`work(tx, out)` as in `transact`), retried once on a lost connection
 */
export function openDatabase(factory, { name, version, upgrade: upgradeDb, label = name }) {
  let dbPromise = null;

  function open() {
    const attempt = new Promise((resolve, reject) => {
      let request;
      try {
        request = factory.open(name, version);
      } catch (error) {
        reject(openRefused(error));
        return;
      }
      request.onupgradeneeded = () => upgradeDb(request.result);
      request.onblocked = () => console.warn(`[${label}] open blocked by another tab holding an older version`);
      request.onerror = () => reject(openRefused(request.error));
      request.onsuccess = () => {
        const db = request.result;
        const forget = () => {
          if (dbPromise === attempt) dbPromise = null;
        };
        db.onversionchange = () => {
          db.close();
          forget();
        };
        db.onclose = forget;
        resolve(db);
      };
    });
    // A failed open is not cached: the next call tries again.
    attempt.catch(() => {
      if (dbPromise === attempt) dbPromise = null;
    });
    return attempt;
  }

  function db() {
    if (!dbPromise) dbPromise = open();
    // The ceiling fails THIS call only; the open keeps going for the next one.
    return timeout(dbPromise, OPEN_TIMEOUT_MS, 'indexedDB.open');
  }

  async function run(stores, mode, work, options) {
    for (let attempt = 0; ; attempt += 1) {
      const connection = await db();
      try {
        return await transact(connection, stores, mode, work, options);
      } catch (error) {
        if (attempt === 0 && connectionLost(error)) {
          dbPromise = null;
          continue;
        }
        throw error;
      }
    }
  }

  return { run };
}

function createIndexedDbStorage(factory) {
  const { run } = openDatabase(factory, { name: DB_NAME, version: DB_VERSION, upgrade, label: 'collector/db' });

  return {
    kind: 'indexeddb',

    /** This user's days, taps and cards — one read. */
    async load(userId) {
      const all = await run([STORES.DAY, STORES.ACTIONS, STORES.CONFLICTS], 'readonly', (tx, out) => {
        readAll(tx, STORES.DAY, out, 'days');
        readAll(tx, STORES.ACTIONS, out, 'actions');
        readAll(tx, STORES.CONFLICTS, out, 'conflicts');
      });
      return {
        days: (all?.days ?? []).filter((row) => belongsTo(row, userId)),
        actions: (all?.actions ?? []).filter((row) => belongsTo(row, userId)),
        conflicts: (all?.conflicts ?? []).filter((row) => belongsTo(row, userId)),
      };
    },

    /** New taps (or a tap's new state), in one strict transaction. */
    async putActions(rows) {
      if (!rows?.length) return;
      await run(STORES.ACTIONS, 'readwrite', (tx) => {
        const store = tx.objectStore(STORES.ACTIONS);
        for (const row of rows) store.put(row);
      }, STRICT);
    },

    /** A batch's answers and the cards they make, together or not at all. */
    async putVerdicts(rows, { cards = [], dropCardIds = [] } = {}) {
      await run([STORES.ACTIONS, STORES.CONFLICTS], 'readwrite', (tx) => {
        const actions = tx.objectStore(STORES.ACTIONS);
        for (const row of rows ?? []) actions.put(row);
        const conflicts = tx.objectStore(STORES.CONFLICTS);
        for (const id of dropCardIds) conflicts.delete(id);
        for (const card of cards) conflicts.put(card);
      }, STRICT);
    },

    /**
     * A day read landed: store the document (when given) and mark the taps it
     * now contains as caught up, in ONE transaction — a crash between the two
     * would bring answered taps back as unmarked on the next start.
     */
    async acceptDay(record, inReadIds = []) {
      await run([STORES.DAY, STORES.ACTIONS], 'readwrite', (tx) => {
        if (record) tx.objectStore(STORES.DAY).put(record);
        const actions = tx.objectStore(STORES.ACTIONS);
        for (const id of inReadIds) {
          const request = actions.get(id);
          request.onsuccess = () => {
            const row = request.result;
            if (row && row.status !== ACTION_STATUS.PENDING && !row.inRead) actions.put({ ...row, inRead: true });
          };
        }
      });
    },

    async deleteConflict(id) {
      await run(STORES.CONFLICTS, 'readwrite', (tx) => {
        tx.objectStore(STORES.CONFLICTS).delete(id);
      }, STRICT);
    },

    /** One photo row (a Blob inside), in one strict transaction. */
    async putPhoto(row) {
      await run(STORES.PHOTOS, 'readwrite', (tx) => {
        tx.objectStore(STORES.PHOTOS).put(row);
      }, STRICT);
    },

    async deletePhoto(uuid) {
      await run(STORES.PHOTOS, 'readwrite', (tx) => {
        tx.objectStore(STORES.PHOTOS).delete(uuid);
      }, STRICT);
    },

    /**
     * Change ONE photo row in ONE strict transaction: read it, ask `change`,
     * write what it says — so a write from the other container (the Chrome tab
     * and the installed app share this database) can never land between the
     * read and the write. `change(row)` gets the stored row (null when there
     * is none), must be synchronous and free of side effects (a reopened
     * connection runs it again), and returns
     *   - an object: its fields are MERGED over the stored row — a field it
     *     does not name is kept as it was;
     *   - `null`: the row is deleted;
     *   - `undefined`: nothing is written.
     * Resolves with the row as it now stands (null when there is none).
     */
    async patchPhoto(uuid, change) {
      return run(STORES.PHOTOS, 'readwrite', (tx, out) => {
        const store = tx.objectStore(STORES.PHOTOS);
        const request = store.get(uuid);
        request.onsuccess = () => {
          try {
            const row = request.result ?? null;
            const fields = change(row);
            if (fields === undefined) {
              out.value = row;
            } else if (fields === null) {
              if (row) store.delete(uuid);
              out.value = null;
            } else {
              out.value = { ...row, ...fields, uuid };
              store.put(out.value);
            }
          } catch (error) {
            // Nothing half-written: the caller gets this error, not a bare abort.
            out.error = error;
            tx.abort();
          }
        };
      }, STRICT);
    },

    /** This user's photo rows — every kind and status (the Blobs of the queued ones come with them). */
    async loadPhotos(userId) {
      const all = await run(STORES.PHOTOS, 'readonly', (tx, out) => {
        readAll(tx, STORES.PHOTOS, out, 'photos');
      });
      return (all?.photos ?? []).filter((row) => belongsTo(row, userId));
    },

    /** Anything of this user's on the phone: a day, a tap not sent, a photo not uploaded, an invoice filed and not sent. */
    async hasWork(userId) {
      const all = await run([STORES.DAY, STORES.ACTIONS, STORES.PHOTOS], 'readonly', (tx, out) => {
        readAll(tx, STORES.DAY, out, 'days');
        readAll(tx, STORES.ACTIONS, out, 'actions');
        readAll(tx, STORES.PHOTOS, out, 'photos');
      });
      return workIn(all, userId);
    },

    /**
     * Plan close (L-I): delete the plan's day, its answered taps, its uploaded
     * photos and its held invoice papers — only if NOTHING of that plan is
     * still waiting (a tap, a photo, a filed invoice; for any user), checked
     * inside the same transaction as the delete. Conflicts stay until
     * dismissed. Resolves true when it cleared, false when it kept.
     */
    async clearPlan(planId) {
      return run([STORES.DAY, STORES.ACTIONS, STORES.PHOTOS], 'readwrite', (tx, out) => {
        const actions = tx.objectStore(STORES.ACTIONS);
        const photos = tx.objectStore(STORES.PHOTOS);
        const actionRows = actions.getAll();
        actionRows.onsuccess = () => {
          const photoRows = photos.getAll();
          photoRows.onsuccess = () => {
            const plan = planRows(actionRows.result, photoRows.result, planId);
            if (plan.waiting) {
              out.value = false;
              return;
            }
            tx.objectStore(STORES.DAY).delete(String(planId));
            for (const row of plan.actions) actions.delete(row.id);
            for (const row of plan.photos) photos.delete(row.uuid);
            out.value = true;
          };
        };
      });
    },
  };
}

// ── Shared rules (both implementations) ──────────────────────────────────────

/**
 * A row of `photos` the phone still owes the server. An uploaded photo owes
 * nothing. An invoice row owes its FILING, once the collector has made one
 * (`queued`): the backend takes a filing on a completed day, so it keeps that
 * plan's day on the phone until it is sent. A paper only `held` is waiting for
 * the collector, not for the queue — it must never hold a closed plan's day (a
 * day he never filed is not worth keeping).
 */
function isWaitingPhoto(row) {
  if (row.kind === PHOTO_KIND.INVOICE) return row.status === PHOTO_QUEUED;
  return row.status !== PHOTO_UPLOADED;
}

function workIn(all, userId) {
  if ((all?.days ?? []).some((row) => belongsTo(row, userId))) return true;
  if ((all?.actions ?? []).some((row) => belongsTo(row, userId) && row.status === ACTION_STATUS.PENDING)) return true;
  return (all?.photos ?? []).some(isWaitingPhoto);
}

function planRows(actionRows = [], photoRows = [], planId) {
  const actions = actionRows.filter((row) => sameId(row.planId, planId));
  const photos = photoRows.filter((row) => sameId(row.planId, planId));
  const waiting = actions.some((row) => row.status === ACTION_STATUS.PENDING) || photos.some(isWaitingPhoto);
  // Everything of the plan that owes nothing goes with it: uploaded photos
  // (their Blob is long gone) and the invoice papers held with no filing. A
  // filing still waiting makes the whole plan wait, so nothing is deleted.
  return { actions, photos: photos.filter((row) => !isWaitingPhoto(row)), waiting };
}

// ── The in-memory twin ───────────────────────────────────────────────────────

const clone = (value) => structuredClone(value);

// A Blob is immutable, so the twin shares it instead of cloning it (a
// structured clone of a Blob is not available in every test engine).
function clonePhoto(row) {
  const { blob, ...rest } = row;
  const copy = clone(rest);
  if (blob !== undefined) copy.blob = blob;
  return copy;
}

export function createMemoryStorage() {
  const stores = {
    [STORES.DAY]: new Map(),
    [STORES.ACTIONS]: new Map(),
    [STORES.PHOTOS]: new Map(),
    [STORES.CONFLICTS]: new Map(),
  };
  const rows = (name) => [...stores[name].values()];
  // Writes settle on a later task, like an IndexedDB transaction does.
  const settle = (fn) => Promise.resolve().then(fn);

  return {
    kind: 'memory',
    stores,

    load: (userId) =>
      settle(() => ({
        days: rows(STORES.DAY).filter((row) => belongsTo(row, userId)).map(clone),
        actions: rows(STORES.ACTIONS).filter((row) => belongsTo(row, userId)).map(clone),
        conflicts: rows(STORES.CONFLICTS).filter((row) => belongsTo(row, userId)).map(clone),
      })),

    putActions: (list) =>
      settle(() => {
        const copies = (list ?? []).map(clone); // all or nothing, like one transaction
        for (const row of copies) stores[STORES.ACTIONS].set(row.id, row);
      }),

    putVerdicts: (list, { cards = [], dropCardIds = [] } = {}) =>
      settle(() => {
        const actionCopies = (list ?? []).map(clone);
        const cardCopies = cards.map(clone);
        for (const row of actionCopies) stores[STORES.ACTIONS].set(row.id, row);
        for (const id of dropCardIds) stores[STORES.CONFLICTS].delete(id);
        for (const card of cardCopies) stores[STORES.CONFLICTS].set(card.id, card);
      }),

    acceptDay: (record, inReadIds = []) =>
      settle(() => {
        const copy = record ? clone(record) : null;
        if (copy) stores[STORES.DAY].set(String(copy.planId), copy);
        for (const id of inReadIds) {
          const row = stores[STORES.ACTIONS].get(id);
          if (row && row.status !== ACTION_STATUS.PENDING && !row.inRead) {
            stores[STORES.ACTIONS].set(id, { ...row, inRead: true });
          }
        }
      }),

    deleteConflict: (id) =>
      settle(() => {
        stores[STORES.CONFLICTS].delete(id);
      }),

    putPhoto: (row) =>
      settle(() => {
        const copy = clonePhoto(row);
        stores[STORES.PHOTOS].set(copy.uuid, copy);
      }),

    deletePhoto: (uuid) =>
      settle(() => {
        stores[STORES.PHOTOS].delete(uuid);
      }),

    // Read, ask, write in one settled step — the twin of the one transaction.
    patchPhoto: (uuid, change) =>
      settle(() => {
        const stored = stores[STORES.PHOTOS].get(uuid);
        const row = stored ? clonePhoto(stored) : null;
        const fields = change(row);
        if (fields === undefined) return row;
        if (fields === null) {
          stores[STORES.PHOTOS].delete(uuid);
          return null;
        }
        const next = clonePhoto({ ...stored, ...fields, uuid });
        stores[STORES.PHOTOS].set(uuid, next);
        return clonePhoto(next);
      }),

    loadPhotos: (userId) =>
      settle(() =>
        rows(STORES.PHOTOS)
          .filter((row) => belongsTo(row, userId))
          .map(clonePhoto),
      ),

    hasWork: (userId) =>
      settle(() =>
        workIn({ days: rows(STORES.DAY), actions: rows(STORES.ACTIONS), photos: rows(STORES.PHOTOS) }, userId),
      ),

    clearPlan: (planId) =>
      settle(() => {
        const plan = planRows(rows(STORES.ACTIONS), rows(STORES.PHOTOS), planId);
        if (plan.waiting) return false;
        stores[STORES.DAY].delete(String(planId));
        for (const row of plan.actions) stores[STORES.ACTIONS].delete(row.id);
        for (const row of plan.photos) stores[STORES.PHOTOS].delete(row.uuid);
        return true;
      }),
  };
}

// ── The storage the app uses ─────────────────────────────────────────────────

let active = null;

/**
 * IndexedDB, falling back to the in-memory twin from the first refusal that
 * counts on (an old private window): `methods` are wrapped one by one. A slow
 * open is not a refusal: that call fails and the caller keeps its data in
 * memory. Shared with the preview copies (collector/previewStore.js).
 *
 * @param {object} idb - the IndexedDB implementation
 * @param {object} memory - its in-memory twin (same methods)
 * @param {string[]} methods
 * @param {string} label - the console prefix
 */
export function withMemoryFallback(idb, memory, methods, label) {
  let refused = false;
  /** Opens refused in a row: a transient backing-store error is not a verdict. */
  let refusals = 0;
  const REFUSALS_BEFORE_MEMORY = 3;
  function call(method) {
    return async (...args) => {
      if (refused) return memory[method](...args);
      try {
        const result = await idb[method](...args);
        refusals = 0;
        return result;
      } catch (error) {
        if (!error?.openRefused && error?.name !== 'SecurityError') throw error;
        refusals += 1;
        // One refused open can be a transient backing-store error (Android):
        // that call fails (the caller keeps the tap in memory) and the next
        // one opens again. Only a SecurityError, or refusals in a row, mean
        // the browser will not give us IndexedDB at all (an older private
        // window): then the session goes on in memory.
        if (error?.name !== 'SecurityError' && refusals < REFUSALS_BEFORE_MEMORY) throw error;
        refused = true;
        console.warn(`[${label}] IndexedDB refused — kept in memory only, gone after a restart`, error);
        return memory[method](...args);
      }
    };
  }

  const storage = {
    get kind() {
      return refused ? 'memory' : 'indexeddb';
    },
  };
  for (const method of methods) storage[method] = call(method);
  return storage;
}

/**
 * IndexedDB when the browser has it, the memory twin otherwise — and the twin
 * from the first refused open on (an old private window). A slow open is not
 * a refusal: that call fails and the caller keeps the tap in memory.
 */
function createAppStorage() {
  const factory = typeof indexedDB === 'undefined' ? null : indexedDB;
  if (!factory) return createMemoryStorage();

  return withMemoryFallback(
    createIndexedDbStorage(factory),
    createMemoryStorage(),
    [
      'load',
      'putActions',
      'putVerdicts',
      'acceptDay',
      'deleteConflict',
      'putPhoto',
      'deletePhoto',
      'patchPhoto',
      'loadPhotos',
      'hasWork',
      'clearPlan',
    ],
    'collector/db',
  );
}

/** The collector's storage (created on first use). */
export function collectorStorage() {
  if (!active) active = createAppStorage();
  return active;
}

/** Tests: use this storage (a fresh twin when none is given). */
export function setCollectorStorageForTests(storage = createMemoryStorage()) {
  active = storage;
  return storage;
}

let persistAsked = false;

/**
 * Ask the browser once per page session not to evict the collector's storage
 * under disk pressure (auto-granted for an installed app on Android). Logged,
 * never awaited by anything the collector does.
 */
export function requestPersistentStorage() {
  if (persistAsked) return;
  persistAsked = true;
  const storage = typeof navigator === 'undefined' ? null : navigator.storage;
  if (typeof storage?.persist !== 'function') return;
  Promise.resolve(typeof storage.persisted === 'function' ? storage.persisted() : false)
    .then((already) => (already ? true : storage.persist()))
    .then((granted) => console.info('[collector/db] persistent storage:', granted ? 'granted' : 'not granted'))
    .catch((error) => console.warn('[collector/db] persistent storage request failed', error));
}
